import { Briefcase, CheckCircle, Download, Edit3 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { Resume } from '../../types';
import './ResumeView.css';

const ResumeView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [resume, setResume] = useState<Resume | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchResume = async () => {
    try {
      setIsLoading(true);
      
      // First fetch resume data
      const resumeResponse = await api.getResume(id!);
      const resumeData = resumeResponse.resume || resumeResponse;
      setResume(resumeData);
      
      // Ensure sectionOrder has all sections (for older resumes missing certifications)
      const defaultSections = ['personal', 'summary', 'experience', 'projects', 'education', 'skills', 'certifications'];
      const loadedSections = resumeData.customization?.sectionOrder || defaultSections;
      const missingSections = defaultSections.filter(s => !loadedSections.includes(s));
      const mergedSectionOrder = [...loadedSections, ...missingSections];
      
      const mergedCustomization = {
        ...resumeData.customization,
        sectionOrder: mergedSectionOrder,
      };
      
      // Generate fresh preview with merged customization
      const htmlContent = await api.previewResumeWithCustomization(
        id!,
        resumeData.data,
        mergedCustomization
      );
      
      setPreviewHtml(htmlContent);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch resume:', err);
      setError(err.response?.data?.error || 'Failed to load resume');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!id) {
      navigate('/dashboard');
      return;
    }

    fetchResume();
    // fetchResume is stable within this component and depends on id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const handleDownload = async () => {
    if (!resume) return;

    try {
      const blob = await api.downloadResume(resume.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resume_${resume.data.personal.name.replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download resume:', err);
      alert('Failed to download resume. Please try again.');
    }
  };


  if (isLoading) {
    return (
      <div className="resume-view-container">
        <div className="loading">Generating your resume...</div>
      </div>
    );
  }

  if (error || !resume) {
    return (
      <div className="resume-view-container">
        <div className="error-message">
          <h2>Unable to Load Resume</h2>
          <p>{error || 'Resume not found'}</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="resume-view-container">
      <div className="resume-header">
        <div className="header-content">
          <div className="header-badge">
            <CheckCircle size={20} />
            <span>Resume Ready</span>
          </div>
          <h1>Your Professional Resume</h1>
          <p className="text-secondary">
            Template: {resume.templateId.charAt(0).toUpperCase() + resume.templateId.slice(1)}
          </p>
        </div>
        
        <div className="action-buttons">
          <button onClick={() => navigate(`/job-analyzer/${id}`)} className="btn btn-secondary">
            <Briefcase size={16} />
            Analyze for Job
          </button>
          <button onClick={() => navigate(`/resume/${id}/edit`)} className="btn btn-secondary">
            <Edit3 size={16} />
            Edit Resume
          </button>
          <button onClick={handleDownload} className="btn btn-primary">
            <Download size={16} />
            Download PDF
          </button>
        </div>
      </div>

      <div className="resume-preview">
        <div className="resume-paper">
          <iframe
            srcDoc={previewHtml}
            title="Resume Preview"
            className="resume-iframe"
            sandbox="allow-same-origin"
          />
        </div>
      </div>

      <div className="resume-footer">
        <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
          Back to Dashboard
        </button>
        <button onClick={() => navigate('/new-journey')} className="btn btn-primary">
          Create Another Resume
        </button>
      </div>
    </div>
  );
};

export default ResumeView;

